/** Nurse CareHub — procedure catalog, limits, and availability defaults. */

import { resolveNpidCode, formatNcidForDisplay } from "../utils/ncidClient";
import { NURSE_HOURLY_PROCEDURES } from "./nemaHourlyServiceList";

export const NURSE_WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const NURSE_MAX_DAILY_HOURS_DEFAULT = 10;
export const NURSE_MAX_DAILY_HOURS_MAX = 16;
export const NURSE_MIN_REST_HOURS_DEFAULT = 1;
export const NURSE_MIN_REST_HOURS_MAX = 8;
export const NURSE_MAX_BOOKINGS_DEFAULT = 5;
export const NURSE_MAX_BOOKINGS_MAX = 10;
export const NURSE_SERVICE_RADIUS_DEFAULT_KM = 20;
export const NURSE_SERVICE_RADIUS_MAX_KM = 25;
export const NURSE_SERVICE_RADIUS_MIN_KM = 1;
export const NURSE_DEFAULT_DAY_START = "09:00";
export const NURSE_DEFAULT_DAY_END = "18:00";
export const NURSE_TRAVEL_BUFFER_DISPLAY_MIN = 25;

/** Leave reason tags stored on caregiver_leave.reason */
export const NURSE_LEAVE_REASON = "LEAVE";
export const NURSE_BLOCK_REASON = "BLOCK";

/**
 * Nursing procedures offered on CareHub.
 * Prices / ranges from NEMA hourly service list draft (see nemaHourlyServiceList.js).
 * rangeMin/Max are reference bands shown beside editable caregiver rates.
 */
export const NURSE_PROCEDURES = NURSE_HOURLY_PROCEDURES.map((p) => ({ ...p }));

/** Sidebar for nurses — same CareHub menus as care attendants. */
export const NURSE_SIDEBAR_ITEMS = [
  { to: "/caregiver", label: "Dashboard", end: true, icon: "grid" },
  { to: "/caregiver/start-end-service", label: "Start / End Service", icon: "clock" },
  { to: "/caregiver/my-bookings", label: "My Bookings", icon: "list" },
  { to: "/caregiver/subscription", label: "Subscription & Credits", icon: "card" },
  { to: "/caregiver/payments", label: "Payments", icon: "rupee" },
  { to: "/caregiver/my-schedule", label: "My Schedule", icon: "calendar" },
  { to: "/caregiver/availability", label: "Availability Calendar", icon: "calendar" },
  { to: "/caregiver/care-notes", label: "Care Notes", icon: "document" },
  { to: "/caregiver/earnings", label: "Earnings", icon: "wallet" },
  { to: "/caregiver/complete-profile", label: "Profile", icon: "user" },
  { to: "/caregiver/pricing", label: "Pricing & Packages", icon: "rupee" },
  { to: "/caregiver/support", label: "Help & Support", icon: "help" },
];

/** Category grouping for the hub pricing table. */
export const NURSE_PROCEDURE_CATEGORIES = [
  { id: "injection",          category: "Injections & IV" },
  { id: "iv_drip",            category: "Injections & IV" },
  { id: "cannulation",        category: "Injections & IV" },
  { id: "iv_therapy",         category: "Injections & IV" },
  { id: "dressing_simple",    category: "Wound & Stoma Care" },
  { id: "dressing_complex",   category: "Wound & Stoma Care" },
  { id: "stoma",              category: "Wound & Stoma Care" },
  { id: "colostomy",          category: "Wound & Stoma Care" },
  { id: "peg_tube",           category: "Wound & Stoma Care" },
  { id: "tracheostomy",       category: "Wound & Stoma Care" },
  { id: "catheter_insert",    category: "Catheter & Tubes" },
  { id: "catheter_remove",    category: "Catheter & Tubes" },
  { id: "catheter_care",      category: "Catheter & Tubes" },
  { id: "urine_bag",          category: "Catheter & Tubes" },
  { id: "ryles_tube_insert",  category: "Catheter & Tubes" },
  { id: "ryles_tube_feed",    category: "Catheter & Tubes" },
  { id: "enema",              category: "Catheter & Tubes" },
  { id: "bp_monitoring",      category: "Monitoring & Vitals" },
  { id: "sugar_monitoring",   category: "Monitoring & Vitals" },
  { id: "vitals",             category: "Monitoring & Vitals" },
  { id: "ecg",                category: "Monitoring & Vitals" },
  { id: "nebulization",       category: "Respiratory" },
  { id: "oxygen",             category: "Respiratory" },
  { id: "suctioning",         category: "Respiratory" },
  { id: "medication",         category: "Medication" },
  { id: "companion_hospital", category: "Companion Nursing" },
];

export function createEmptyNurseProcedureMap() {
  const map = {};
  for (const p of NURSE_PROCEDURES) {
    map[p.id] = {
      offered: true,
      priceInr: p.defaultPriceInr,
    };
  }
  return map;
}

export function mergeNurseProcedureMap(raw) {
  const base = createEmptyNurseProcedureMap();
  if (!raw || typeof raw !== "object") return base;
  const next = { ...base };
  for (const p of NURSE_PROCEDURES) {
    const row = raw[p.id];
    if (!row || typeof row !== "object") continue;
    next[p.id] = {
      offered: row.offered !== false,
      priceInr:
        Number.isFinite(Number(row.priceInr)) && Number(row.priceInr) > 0
          ? Math.round(Number(row.priceInr))
          : p.defaultPriceInr,
    };
  }
  return next;
}

export function formatNurseProviderId(npid) {
  const code = resolveNpidCode(npid).toUpperCase();
  if (code) {
    if (code.startsWith("NCID-") || code.startsWith("NPID-")) return formatNcidForDisplay(code);
    const legacy = code.match(/^INC-(\d{2})(\d{5})$/);
    if (legacy) return `NCID-${legacy[1]}-RN-${legacy[2]}`;
    return code;
  }
  return "NCID pending verification";
}
